import { useState, useEffect, useCallback } from "react";

const API_BASE = "https://vedonica.in/api/blog";

const api = {
  getPosts: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/posts/${q ? "?" + q : ""}`).then((r) => {
      if (!r.ok) throw new Error("Posts load nahi hue");
      return r.json();
    });
  },
  getPost: (id) =>
    fetch(`${API_BASE}/posts/${id}/`).then((r) => {
      if (!r.ok) throw new Error("Post nahi mili");
      return r.json();
    }),
  getCategories: () =>
    fetch(`${API_BASE}/categories/`).then((r) => r.json()),
};

const G = {
  green:     "#3d9970",
  light:     "#e8f5ee",
  mid:       "#c1e8cf",
  dark:      "#1a5c3a",
  white:     "#ffffff",
  soft:      "#f7fcf9",
  text:      "#1a2e22",
  textMid:   "#3a5244",
  textLight: "#5a7a68",
  textMuted: "#7aab8f",
};

const tagColors = {
  New:      { bg: "#d1fae5", color: "#065f46" },
  Popular:  { bg: "#fef9c3", color: "#713f12" },
  Featured: { bg: "#e0f2fe", color: "#0c4a6e" },
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
};

function SectionLabel({ text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
      <div style={{ width: 4, height: 22, background: G.green, borderRadius: 2 }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: G.green, letterSpacing: 2, textTransform: "uppercase" }}>
        {text}
      </span>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "64px 0" }}>
      <div style={{
        width: 40, height: 40, border: `3px solid ${G.mid}`,
        borderTop: `3px solid ${G.green}`, borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{ textAlign: "center", padding: "64px 24px" }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🌿</div>
      <p style={{ color: G.textLight, marginBottom: 20 }}>{message}</p>
      {onRetry && (
        <button onClick={onRetry} style={{ background: G.green, color: G.white, border: "none", borderRadius: 30, padding: "10px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
        try again please
        </button>
      )}
    </div>
  );
}

function BlogListing({ onSelectPost }) {
  const [activeCategory, setActiveCategory] = useState("All");
  const [categories, setCategories] = useState(["All"]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const isMobile = useIsMobile();

  useEffect(() => {
    api.getCategories()
      .then((data) => {
        const cats = (data.results || data).map((c) => c.name);
        setCategories(["All", ...cats]);
      })
      .catch(() => {});
  }, []);

  const loadPosts = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { page };
    if (activeCategory !== "All") params.category = activeCategory;
    if (searchQuery) params.search = searchQuery;
    api.getPosts(params)
      .then((data) => { setPosts(data.results || []); setTotalCount(data.count || 0); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [activeCategory, searchQuery, page]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleCategoryChange = (cat) => { setActiveCategory(cat); setPage(1); };
  const handleSearch = (e) => { e.preventDefault(); setSearchQuery(searchInput); setPage(1); };

  const featured = posts[0];
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div style={{ fontFamily: "'Georgia','Garamond',serif", background: G.soft, color: G.text, minHeight: "100vh" }}>

      <section style={{ background: `linear-gradient(135deg,${G.dark} 0%,${G.green} 60%,#52b788 100%)`, padding: isMobile ? "48px 20px 36px" : "72px 24px 52px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 20% 50%,rgba(255,255,255,.07) 0%,transparent 50%),radial-gradient(circle at 80% 20%,rgba(255,255,255,.05) 0%,transparent 40%)" }} />
        <div style={{ position: "relative", maxWidth: 640, margin: "0 auto" }}>
          <div style={{ display: "inline-block", background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.3)", borderRadius: 20, padding: "4px 16px", fontSize: 11, color: "#c8f7dc", letterSpacing: 2, marginBottom: 18, textTransform: "uppercase" }}>✦ Our Stories</div>
          <h1 style={{ fontSize: isMobile ? 32 : "clamp(30px,6vw,60px)", fontWeight: 700, color: G.white, margin: "0 0 12px", lineHeight: 1.15 }}>
            The Vedoncia <span style={{ color: "#a8f0c6", fontStyle: "italic" }}>Blog</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,.82)", fontSize: isMobile ? 14 : 16, lineHeight: 1.7, margin: "0 0 24px" }}>
            Insights on fashion, lifestyle, sustainability &amp; the art of dressing well.
          </p>
        </div>
      </section>

      {!loading && !error && featured && activeCategory === "All" && !searchQuery && (
        <section style={{ padding: isMobile ? "28px 16px 0" : "52px 24px 0", maxWidth: 1200, margin: "0 auto" }}>
          <SectionLabel text="Featured Article" />
          <div
            onClick={() => onSelectPost(featured.id)}
            style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(280px,1fr))", background: G.white, borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 32px rgba(61,153,112,.12)", border: `1px solid ${G.mid}`, cursor: "pointer" }}
          >
            <div style={{ minHeight: isMobile ? 200 : 300 }}>
              <img src={featured.image} alt={featured.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div style={{ padding: isMobile ? "20px 18px" : "36px 32px 32px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: G.green, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>{featured.category}</span>
              <h2 style={{ fontSize: isMobile ? 18 : "clamp(18px,2.5vw,24px)", fontWeight: 700, color: G.dark, lineHeight: 1.3, margin: "0 0 14px" }}>{featured.title}</h2>
              <p style={{ color: G.textLight, fontSize: isMobile ? 13 : 15, lineHeight: 1.75, marginBottom: 18 }}>{featured.excerpt}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 13, color: G.textMuted, marginBottom: 22 }}>
                <span>✍️ {featured.author}</span>
                <span>📅 {featured.date}</span>
                <span>⏱ {featured.readTime}</span>
              </div>
              <button style={{ background: G.green, color: G.white, border: "none", borderRadius: 30, padding: "11px 26px", fontSize: 14, fontWeight: 700, cursor: "pointer", alignSelf: "flex-start" }}>Read Article →</button>
            </div>
          </div>
        </section>
      )}

      <section style={{ padding: isMobile ? "24px 16px 0" : "36px 24px 0", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {categories.map((cat) => (
            <button key={cat} onClick={() => handleCategoryChange(cat)}
              style={{ background: activeCategory === cat ? G.green : G.white, color: activeCategory === cat ? G.white : G.textMid, border: `1.5px solid ${activeCategory === cat ? G.green : G.mid}`, borderRadius: 24, padding: "7px 16px", fontSize: 13, cursor: "pointer", fontWeight: activeCategory === cat ? 700 : 500 }}>
              {cat}
            </button>
          ))}
        </div>
      </section>

      <main style={{ padding: isMobile ? "24px 16px 48px" : "36px 24px 64px", maxWidth: 1200, margin: "0 auto" }}>
        {loading ? <Spinner /> : error ? <ErrorBanner message={error} onRetry={loadPosts} /> : posts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "64px 0", color: G.textMuted }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🌿</div>
            <p>Koi article nahi mila.</p>
          </div>
        ) : (
          <>
            <SectionLabel text={`${totalCount} Article${totalCount !== 1 ? "s" : ""}${searchQuery ? ` — "${searchQuery}"` : ""}`} />
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(280px,1fr))", gap: isMobile ? 16 : 24 }}>
              {posts.map((post) => (
                <article key={post.id} onClick={() => onSelectPost(post.id)}
                  style={{ background: G.white, borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(61,153,112,.08)", border: `1px solid ${G.mid}`, display: "flex", flexDirection: "column", cursor: "pointer" }}>
                  <div style={{ position: "relative", height: 200 }}>
                    <img src={post.image} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    {post.tag && (
                      <span style={{ position: "absolute", top: 12, left: 12, borderRadius: 12, padding: "3px 12px", fontSize: 11, fontWeight: 700, letterSpacing: 1, background: tagColors[post.tag]?.bg, color: tagColors[post.tag]?.color }}>
                        {post.tag}
                      </span>
                    )}
                  </div>
                  <div style={{ padding: "18px 20px 20px", flex: 1, display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: G.green, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{post.category}</span>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: G.dark, margin: "0 0 10px", lineHeight: 1.35 }}>{post.title}</h3>
                    <p style={{ color: G.textLight, fontSize: 14, lineHeight: 1.7, flex: 1, marginBottom: 14 }}>{post.excerpt}</p>
                    <div style={{ display: "flex", gap: 14, fontSize: 12, color: G.textMuted, marginBottom: 14 }}>
                      <span>✍️ {post.author}</span><span>⏱ {post.readTime}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${G.light}`, paddingTop: 12 }}>
                      <span style={{ fontSize: 12, color: "#9bbdaa" }}>{post.date}</span>
                      <button onClick={(e) => { e.stopPropagation(); onSelectPost(post.id); }}
                        style={{ background: "transparent", border: `1.5px solid ${G.green}`, borderRadius: 20, padding: "5px 14px", fontSize: 12, color: G.green, fontWeight: 700, cursor: "pointer" }}>
                        Read More →
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 20, marginTop: 48, paddingTop: 32, borderTop: `1px solid ${G.mid}` }}>
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                  style={{ background: G.white, border: `1.5px solid ${G.mid}`, borderRadius: 24, padding: "8px 20px", fontSize: 13, color: G.dark, cursor: "pointer", fontWeight: 600, opacity: page === 1 ? 0.4 : 1 }}>← Pehle</button>
                <span style={{ fontSize: 14, color: G.textMuted }}>Page {page} / {totalPages}</span>
                <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}
                  style={{ background: G.white, border: `1.5px solid ${G.mid}`, borderRadius: 24, padding: "8px 20px", fontSize: 13, color: G.dark, cursor: "pointer", fontWeight: 600, opacity: page === totalPages ? 0.4 : 1 }}>Agle →</button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function BlogDetail({ postId, onBack, onSelectPost }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [tocOpen, setTocOpen] = useState(false);
  const isMobile = useIsMobile();

  const loadPost = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getPost(postId)
      .then((data) => {
        setPost(data);
        return api.getPosts({ category: data.category });
      })
      .then((data) => {
        const related = (data.results || []).filter((p) => p.id !== postId).slice(0, 3);
        setRelatedPosts(related);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [postId]);

  useEffect(() => {
    loadPost();
    window.scrollTo(0, 0);
  }, [postId, loadPost]);

  const renderBlock = (block, i) => {
    const type = block.type || block.block_type;
    switch (type) {
      case "intro":
        return (
          <p key={i} style={{ fontSize: isMobile ? 16 : 18, lineHeight: 1.85, color: "#2e4a38", fontStyle: "italic", borderLeft: `4px solid ${G.green}`, paddingLeft: 20, marginBottom: 36 }}>
            {block.text}
          </p>
        );
      case "heading":
        return (
          <h2 key={i} style={{ fontSize: isMobile ? 18 : "clamp(17px,2.5vw,23px)", fontWeight: 700, color: G.dark, margin: "36px 0 14px", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: G.green, flexShrink: 0 }}>—</span> {block.text}
          </h2>
        );
      case "paragraph":
        return <p key={i} style={{ fontSize: isMobile ? 15 : 16, lineHeight: 1.9, color: G.textMid, marginBottom: 22 }}>{block.text}</p>;
      case "quote":
        return (
          <blockquote key={i} style={{ background: G.light, borderLeft: `5px solid ${G.green}`, borderRadius: "0 12px 12px 0", padding: isMobile ? "16px 18px" : "24px 28px", margin: "32px 0" }}>
            <p style={{ fontSize: isMobile ? 16 : 18, fontStyle: "italic", color: G.dark, lineHeight: 1.7, margin: "0 0 10px" }}>"{block.text}"</p>
            <cite style={{ fontSize: 13, color: G.green, fontStyle: "normal", fontWeight: 700 }}>— {block.attribution}</cite>
          </blockquote>
        );
      case "image":
        return (
          <figure key={i} style={{ margin: "32px 0" }}>
            <img src={block.src} alt={block.caption} style={{ width: "100%", borderRadius: 12, display: "block", objectFit: "cover", maxHeight: isMobile ? 240 : 400 }} />
            <figcaption style={{ textAlign: "center", fontSize: 13, color: G.textMuted, marginTop: 10, fontStyle: "italic" }}>{block.caption}</figcaption>
          </figure>
        );
      default: return null;
    }
  };

  if (loading) return (
    <div style={{ fontFamily: "'Georgia','Garamond',serif", background: G.soft, minHeight: "100vh" }}>
      <div style={{ background: G.white, borderBottom: `1px solid ${G.mid}`, padding: isMobile ? "16px 16px 0" : "16px 24px 0" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 16 }}>
          <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: G.light, border: `1.5px solid ${G.mid}`, borderRadius: 24, padding: "19px 20px", fontSize: 14, color: G.dark, fontWeight: 700, cursor: "pointer" }}>
            ← Back to Blog
          </button>
        </div>
      </div>
      <Spinner />
    </div>
  );

  if (error || !post) return (
    <div style={{ fontFamily: "'Georgia','Garamond',serif", background: G.soft, minHeight: "100vh" }}>
      <div style={{ background: G.white, borderBottom: `1px solid ${G.mid}`, padding: isMobile ? "16px 16px 0" : "16px 24px 0" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 16 }}>
          <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: G.light, border: `1.5px solid ${G.mid}`, borderRadius: 24, padding: "19px 20px", fontSize: 14, color: G.dark, fontWeight: 700, cursor: "pointer" }}>
            ← Back to Blog
          </button>
        </div>
      </div>
      <ErrorBanner message={error || "Post nahi mili"} onRetry={loadPost} />
    </div>
  );

  const contentBlocks = post.content || [];
  const headings = contentBlocks.filter((b) => (b.type || b.block_type) === "heading");

  return (
    <div style={{ fontFamily: "'Georgia','Garamond',serif", background: G.soft, color: G.text, minHeight: "100vh" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 767px) {
          .article-grid { grid-template-columns: 1fr !important; }
          .sidebar { position: static !important; }
        }
      `}</style>

      {/* BACK BUTTON — proper padded, pill style */}
      <div style={{ background: G.white, borderBottom: `1px solid ${G.mid}`, padding: isMobile ? "36px 7px 6px 10px" : "36px 7px 6px 10px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <button onClick={onBack}
  style={{ display: "inline-flex", alignItems: "center", gap: 8, background: G.light, border: `1.5px solid ${G.mid}`, borderRadius: 24, padding: isMobile ? "8px 16px" : "10px 20px", fontSize: 14, color: G.dark, fontWeight: 700, cursor: "pointer", transition: "all .2s" }}
  onMouseEnter={e => { e.currentTarget.style.background = G.mid; }}
  onMouseLeave={e => { e.currentTarget.style.background = G.light; }}
>
            <span style={{ fontSize: 16 }}>←</span> Back to Blog
          </button>
        </div>
      </div>

      {/* HERO IMAGE */}
      <div style={{ position: "relative", height: isMobile ? 240 : "clamp(300px,50vw,500px)", overflow: "hidden" }}>
        <img src={post.image} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(10,40,20,.88) 0%,rgba(10,40,20,.35) 55%,transparent 100%)" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: isMobile ? "20px 16px" : "40px clamp(20px,5vw,80px)" }}>
          <span style={{ display: "inline-block", background: G.green, color: G.white, borderRadius: 20, padding: "4px 14px", fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 12 }}>{post.category}</span>
          <h1 style={{ fontSize: isMobile ? 20 : "clamp(22px,4vw,42px)", fontWeight: 700, color: G.white, margin: "0 0 10px", lineHeight: 1.25, maxWidth: 800 }}>{post.title}</h1>
          {!isMobile && (
            <p style={{ color: "rgba(255,255,255,.82)", fontSize: "clamp(14px,2vw,16px)", lineHeight: 1.65, margin: 0, maxWidth: 680 }}>{post.excerpt}</p>
          )}
        </div>
      </div>

      {/* AUTHOR META BAR */}
      <div style={{ background: G.white, borderBottom: `2px solid ${G.light}` }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: isMobile ? "12px 16px" : "14px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {post.authorImg && (
              <img src={post.authorImg} alt={post.author} style={{ width: isMobile ? 36 : 44, height: isMobile ? 36 : 44, borderRadius: "50%", objectFit: "cover", border: `2px solid ${G.mid}` }} />
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: isMobile ? 13 : 15, color: G.dark }}>{post.author}</div>
              <div style={{ fontSize: 12, color: G.textMuted }}>{post.authorRole}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: G.textMuted, flexWrap: "wrap" }}>
            <span>📅 {post.date}</span>
            <span style={{ color: G.mid }}>·</span>
            <span>⏱ {post.readTime}</span>
          </div>
        </div>
      </div>

      {/* MOBILE EXCERPT */}
      {isMobile && post.excerpt && (
        <div style={{ background: G.light, borderBottom: `1px solid ${G.mid}`, padding: "14px 16px" }}>
          <p style={{ fontSize: 14, color: G.textMid, lineHeight: 1.65, margin: 0, fontStyle: "italic" }}>{post.excerpt}</p>
        </div>
      )}

      {/* MOBILE TOC ACCORDION */}
      {isMobile && headings.length > 0 && (
        <div style={{ margin: "16px 16px 0", background: G.white, border: `1px solid ${G.mid}`, borderRadius: 12, overflow: "hidden" }}>
          <button onClick={() => setTocOpen(v => !v)}
            style={{ width: "100%", background: "none", border: "none", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: G.dark, cursor: "pointer" }}>
            <span>📋 In This Article</span>
            <span style={{ transition: "transform .3s", display: "inline-block", transform: tocOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
          </button>
          {tocOpen && (
            <ul style={{ listStyle: "none", padding: "0 16px 14px", margin: 0 }}>
              {headings.map((b, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: G.textMid, padding: "7px 0", borderBottom: `1px solid ${G.light}`, lineHeight: 1.45 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: G.green, flexShrink: 0, marginTop: 5, display: "block" }} />
                  {b.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ARTICLE + SIDEBAR */}
      <div style={{ padding: isMobile ? "20px 16px 32px" : "48px 24px 32px", maxWidth: 1200, margin: "0 auto" }}>
        <div className="article-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 300px", gap: isMobile ? 24 : 48, alignItems: "start" }}>

          <article>
            {contentBlocks.map((block, i) => renderBlock(block, i))}

            {post.tags && post.tags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, margin: "36px 0 28px" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: G.textMuted }}>Tags:</span>
                {post.tags.map((t) => (
                  <span key={t} style={{ background: G.light, border: `1px solid ${G.mid}`, borderRadius: 20, padding: "4px 14px", fontSize: 12, color: G.dark, fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            )}

            {/* AUTHOR CARD */}
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 14 : 20, background: G.white, border: `1px solid ${G.mid}`, borderRadius: 16, padding: isMobile ? 18 : 24, alignItems: isMobile ? "center" : "flex-start", textAlign: isMobile ? "center" : "left", marginTop: 36 }}>
              {post.authorImg && (
                <img src={post.authorImg} alt={post.author} style={{ width: isMobile ? 64 : 70, height: isMobile ? 64 : 70, borderRadius: "50%", objectFit: "cover", border: `3px solid ${G.mid}`, flexShrink: 0 }} />
              )}
              <div>
                <div style={{ fontSize: 11, color: G.textMuted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>Written by</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: G.dark, marginBottom: 2 }}>{post.author}</div>
                <div style={{ fontSize: 13, color: G.green, marginBottom: 10 }}>{post.authorRole} · Vedoncia International</div>
                <p style={{ fontSize: 14, color: G.textLight, lineHeight: 1.7, margin: 0 }}>
                  {post.author} helps readers discover the benefits of Ayurvedic products, natural ingredients, and holistic wellness through expert insights on {post.category?.toLowerCase()}.
                </p>
              </div>
            </div>
          </article>

          {/* SIDEBAR — desktop only, on mobile TOC is accordion above */}
          {!isMobile && (
            <aside className="sidebar" style={{ position: "sticky", top: 20, display: "flex", flexDirection: "column", gap: 20 }}>
              {headings.length > 0 && (
                <div style={{ background: G.white, border: `1px solid ${G.mid}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: G.dark, marginBottom: 14 }}>📋 In This Article</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {headings.map((b, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: G.textMid, padding: "7px 0", borderBottom: `1px solid ${G.light}`, lineHeight: 1.45 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: G.green, flexShrink: 0, marginTop: 5, display: "block" }} />{b.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            
            </aside>
          )}
        </div>
      </div>

      {/* RELATED POSTS */}
      {relatedPosts.length > 0 && (
        <section style={{ padding: isMobile ? "16px 16px 48px" : "16px 24px 64px", maxWidth: 1200, margin: "0 auto" }}>
          <SectionLabel text="You May Also Like" />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(260px,1fr))", gap: isMobile ? 14 : 22 }}>
            {relatedPosts.map((rp) => (
              <div key={rp.id} onClick={() => onSelectPost(rp.id)}
                style={{ background: G.white, borderRadius: 14, overflow: "hidden", border: `1px solid ${G.mid}`, boxShadow: "0 4px 16px rgba(61,153,112,.07)", display: "flex", flexDirection: isMobile ? "row" : "column", cursor: "pointer" }}>
                <div style={{ height: isMobile ? 90 : 170, width: isMobile ? 110 : "100%", flexShrink: 0 }}>
                  <img src={rp.image} alt={rp.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
                <div style={{ padding: isMobile ? "12px 14px" : "14px 16px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: G.green, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>{rp.category}</span>
                  <h4 style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: G.dark, margin: "0 0 8px", lineHeight: 1.35, flex: 1 }}>{rp.title}</h4>
                  {!isMobile && (
                    <div style={{ display: "flex", gap: 6, fontSize: 12, color: G.textMuted, marginBottom: 12 }}>
                      <span>{rp.date}</span><span>·</span><span>{rp.readTime}</span>
                    </div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); onSelectPost(rp.id); }}
                    style={{ background: "transparent", border: `1.5px solid ${G.green}`, borderRadius: 20, padding: "5px 14px", fontSize: 12, color: G.green, fontWeight: 700, cursor: "pointer", alignSelf: "flex-start" }}>
                    Read →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      
    </div>
  );
}

export default function VedoniaBlog() {
  const [selectedPostId, setSelectedPostId] = useState(null);
  if (selectedPostId !== null) {
    return <BlogDetail postId={selectedPostId} onBack={() => setSelectedPostId(null)} onSelectPost={(id) => setSelectedPostId(id)} />;
  }
  return <BlogListing onSelectPost={(id) => setSelectedPostId(id)} />;
}